import { Box, IconButton, Stack, Typography } from '@mui/material';
import { useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/iconify';
import { VisitTranscriptionDtoTranscriptSummaryStatusEnum } from 'woofwoof-api';
import {
  VisitTranscription,
  VisitTranscriptionActivity,
  VisitTranscriptionSpeaker,
} from 'src/types/visit-transcription';
import {
  numberTranscriptSpeakers,
  transcriptSpeakerKey,
} from 'src/sections/@dashboard/visit/utils/transcriptSpeakers';
import TranscriptChatRow, { SpeakerParty } from './TranscriptChatRow';
import VisitTranscriptionActivityIndicator from './VisitTranscriptionActivityIndicator';

interface VisitTranscriptionContentProps {
  transcription?: VisitTranscription | null;
  partialText?: string;
  isRecording?: boolean;
  activity?: VisitTranscriptionActivity;
  onStart?: () => void;
  professional?: SpeakerParty;
  caregiver?: SpeakerParty;
  /**
   * true (default): newest bubble first (top) — for live recording.
   * false: natural chronological order (oldest first, newest at the bottom) —
   * for read-only review.
   */
  newestFirst?: boolean;
  /**
   * Whose viewpoint to render from — "me" sits on the right in primary colour.
   * Defaults to 'professional' (auth-side staff view); the owner-facing modal
   * passes 'caregiver' so the owner reads on the right (WhatsApp-style).
   */
  perspective?: 'professional' | 'caregiver';
  /**
   * true (default): the transcript list owns its own bounded scroll (maxHeight
   * 60vh) — for the live recording view and standalone modals. false: let the
   * list flow at full height so a parent scroll container is the only scroller
   * (avoids a nested/double scrollbar inside the org visit modal body).
   */
  innerScroll?: boolean;
  /** Fill a bounded visit panel, with messages scrolling above the activity footer. */
  fillHeight?: boolean;
  /** Reveal the latest saved bubble when the visit transcript tab opens. */
  scrollToLatestOnOpen?: boolean;
}

export default function VisitTranscriptionContent({
  transcription,
  partialText,
  isRecording = false,
  activity = isRecording ? 'recording' : 'idle',
  onStart,
  professional,
  caregiver,
  newestFirst = true,
  perspective = 'professional',
  innerScroll = true,
  fillHeight = false,
  scrollToLatestOnOpen = false,
}: VisitTranscriptionContentProps) {
  const { t } = useTranslation();

  const segments = useMemo(
    () => transcription?.segments || [],
    [transcription?.segments],
  );
  const speakerNumbers = numberTranscriptSpeakers(segments);
  const isActive = activity !== 'idle';

  const scrollRef = useRef<HTMLDivElement>(null);
  const wasActiveRef = useRef(false);
  const hasOpenedRef = useRef(false);

  useLayoutEffect(() => {
    // Include the final saved update when the session becomes inactive.
    const followLatest = isActive || isRecording || wasActiveRef.current;
    wasActiveRef.current = isActive || isRecording;
    if (newestFirst) return;
    const el = scrollRef.current;
    if (!el) return;
    if (scrollToLatestOnOpen && !hasOpenedRef.current && segments.length) {
      hasOpenedRef.current = true;
      el.scrollTop = el.scrollHeight;
    } else if (followLatest) el.scrollTop = el.scrollHeight;
  }, [
    newestFirst,
    isActive,
    isRecording,
    segments,
    partialText,
    activity,
    scrollToLatestOnOpen,
  ]);

  if (!segments.length && !partialText && !isActive) {
    // Read-only contexts (no start handler) keep the simple copy.
    if (!onStart) {
      return (
        <Stack sx={{ gap: 1 }}>
          <Typography variant="subtitle2">
            {t('visit.transcriptionTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('visit.transcriptionEmpty')}
          </Typography>
        </Stack>
      );
    }
    return (
      <Stack
        sx={{
          flex: 1,
          minHeight: 320,
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          gap: 2,
          py: 4,
        }}
      >
        <Typography variant="h6">{t('visit.startTranscription')}</Typography>
        <IconButton
          aria-label={String(t('visit.startTranscription'))}
          onClick={() => onStart?.()}
          disabled={!onStart}
          sx={{
            width: 72,
            height: 72,
            // Match the header "Rozpocznij transkrypcję" contained button: icon
            // uses the button colour's contrast text (dark in this theme).
            color: isRecording
              ? 'warning.contrastText'
              : 'primary.contrastText',
            bgcolor: isRecording ? 'warning.main' : 'primary.main',
            '&:hover': {
              bgcolor: isRecording ? 'warning.dark' : 'primary.dark',
            },
          }}
        >
          <Iconify
            icon={
              isRecording ? 'solar:stop-circle-bold' : 'solar:microphone-bold'
            }
            width={32}
          />
        </IconButton>
      </Stack>
    );
  }

  // newestFirst (default): latest bubble at the top, matching the Asystent Vetsi
  // tab — the live conversation stays visible without auto-scroll, interim bubble
  // on top. Otherwise natural chronological order (oldest first, newest at the
  // bottom) for read-only review, interim bubble at the bottom.
  // showHeader marks the first bubble of each consecutive same-speaker run.
  const orderedSegments = newestFirst ? [...segments].reverse() : segments;

  const partialBubble = partialText ? (
    <TranscriptChatRow
      speaker={VisitTranscriptionSpeaker.Unknown}
      text={partialText}
      professional={professional}
      caregiver={caregiver}
      perspective={perspective}
      interim
      animated={isActive}
      showHeader
    />
  ) : null;

  const summaryCallout =
    !isActive && transcription?.transcriptSummary ? (
      <Box
        sx={{
          p: 2,
          mb: 1.5,
          borderRadius: 1,
          bgcolor: 'info.lighter',
          borderLeft: '4px solid',
          borderColor: 'info.main',
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {t('visit.transcriptSummary')}
        </Typography>
        <Typography
          variant="body2"
          sx={{ lineHeight: 1.6, whiteSpace: 'pre-line' }}
        >
          {transcription.transcriptSummary}
        </Typography>
      </Box>
    ) : null;

  const summaryPendingCaption =
    !isActive &&
    !transcription?.transcriptSummary &&
    transcription?.transcriptSummaryStatus ===
      VisitTranscriptionDtoTranscriptSummaryStatusEnum.Pending ? (
      <Typography variant="caption" color="info.main" sx={{ mb: 1 }}>
        {t('visit.summaryGenerating')}
      </Typography>
    ) : null;

  const summaryFailedCaption =
    !isActive &&
    !transcription?.transcriptSummary &&
    transcription?.transcriptSummaryStatus ===
      VisitTranscriptionDtoTranscriptSummaryStatusEnum.Failed ? (
      <Typography variant="caption" color="text.disabled" sx={{ mb: 1 }}>
        {t('visit.summaryGenerationFailed')}
      </Typography>
    ) : null;

  return (
    <Stack
      sx={{
        minHeight: 0,
        ...(fillHeight
          ? { flex: 1, height: '100%', overflow: 'hidden' }
          : innerScroll && { maxHeight: '60vh' }),
        pr: 0.5,
      }}
    >
      <Stack
        ref={scrollRef}
        sx={{
          minHeight: 0,
          ...((innerScroll || fillHeight) && {
            flex: 1,
            overflowY: 'auto',
          }),
        }}
      >
        {summaryCallout}
        {summaryPendingCaption}
        {summaryFailedCaption}
        {newestFirst && partialBubble}
        {orderedSegments.map((segment, index) => (
          <TranscriptChatRow
            key={segment.id}
            speaker={segment.speaker}
            speakerNumber={speakerNumbers.get(transcriptSpeakerKey(segment))}
            text={segment.text}
            animated={isActive}
            professional={professional}
            caregiver={caregiver}
            perspective={perspective}
            time={segment.createdAt}
            showHeader={
              index === 0 ||
              transcriptSpeakerKey(orderedSegments[index - 1]) !==
                transcriptSpeakerKey(segment)
            }
          />
        ))}
        {!newestFirst && partialBubble}
      </Stack>
      <VisitTranscriptionActivityIndicator
        activity={activity}
        expanded={!segments.length && !partialText}
      />
    </Stack>
  );
}
